/** Client for the Cloudflare Worker API (play/src/worker). All POSTs carry the Supabase JWT. */
import { currentAccessToken } from './auth';

export interface PriceInfo { amount: number; currency: string; display: string }
export interface Prices {
  currency: string;
  weekly: PriceInfo;
  annual: PriceInfo & { perWeek: string; perWeekAmount?: number };
  exitWeeklyIntro: PriceInfo; // first week price, then weekly
  retentionAnnual: PriceInfo; // first year price, then annual
  /** Pricing arm served by the Worker (C on the web). */
  arm?: 'A' | 'B' | 'C' | 'D';
  /** 'weekly_intro' → the exit offer is the 1st-week price; 'annual_recurring' arms have no web exit offer. */
  exitType?: 'weekly_intro' | 'annual_recurring';
  /** Weekly price after the exit offer's first week. */
  exitWeeklyRenewal?: PriceInfo;
  /** Annual price after the retention offer's first year. */
  retentionRenewal?: PriceInfo;
  /** true when the Worker has no Stripe key (dev fixture). */
  fixture?: boolean;
}
export type Plan = 'weekly' | 'annual';
export type Offer = 'exit_weekly_intro' | 'retention_annual_999';
export type RetentionSignal = 'refresh' | 'acquisition_paywall_declined' | 'purchase_cancelled' | 'expired_subscription';
export type RetentionPlacement = 'refresh' | 'home_quick_action' | 'post_paywall_abandon';
export interface RetentionEvaluation { eligible: boolean; arm?: 'offer_999' | 'holdout'; product_id: string; reason?: string }

async function call<T>(path: string, body?: unknown, auth = true): Promise<T> {
  const headers: Record<string, string> = { 'content-type': 'application/json' };
  if (auth) { const token = await currentAccessToken(); if (!token) throw new Error('Not signed in'); headers.authorization = `Bearer ${token}`; }
  const res = await fetch(path, { method: body === undefined ? 'GET' : 'POST', headers, body: body === undefined ? undefined : JSON.stringify(body) });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return (await res.json()) as T;
}

export class ApiError extends Error { constructor(public status: number, message: string) { super(message); } }

export const api = {
  prices: (locale: string) => call<Prices>(`/api/prices?locale=${encodeURIComponent(locale)}`, undefined, false),
  checkout: (input: { plan: Plan; offer?: Offer; locale: string; returnPath: string }) => call<{ url: string }>('/api/checkout', input),
  portal: (input: { returnPath?: string; locale?: string } = {}) => call<{ url: string }>('/api/portal', input),
  /** Web-only: cancels any Stripe subscription, then deletes the account (see src/worker/account.ts). */
  deleteAccount: () => call<{ ok: true; cancelledSubscriptions: number }>('/api/account/delete', {}),
  retentionEvaluate: (input: { signal: RetentionSignal; placement: RetentionPlacement }) => call<RetentionEvaluation>('/api/retention/evaluate', input),
};
